import cx from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import parse from 'html-react-parser';
import { Editor } from 'react-draft-wysiwyg';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Dispatch, TextBlock } from "../../types";
import { ContentState, convertToRaw, EditorState } from "draft-js";

import './TextEditor.scss';
import { Color } from "../../../../../zoning/components/Zoning/helpers";
import FontVariant from "./FontVariant";
import draftToHtml from "./draftToHtml";
import htmlToDraft from "./html-to-draftjs"

type Props = Readonly<{
  block: TextBlock;
  active: boolean;
  disabled: boolean;
  dispatch: Dispatch;
}>;

export const TextEditor = ({ block, disabled, dispatch, active }: Props) => {
  const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty());
  
  useEffect(() => {
    if (active) {
      const blocksFromHtml = htmlToDraft(convertInnodomToHtml(block.text));
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);

      var editorState = EditorState.createWithContent(contentState);

      setEditorState(editorState);
    }
  }, [active]);

  const setEditorReference = (ref) => {
    if (ref) {
      setTimeout(() => ref.focus(), 10)
    }
  }
  
  const editorWrapperClassName = cx("editor");

  return (
    <div className={editorWrapperClassName}>
      {!active && <span>{parse(convertInnodomToHtml(block.text))}</span>}
      {active && <Editor
        editorRef={setEditorReference}
        editorState={editorState}
        wrapperClassName="wrapper-class"
        editorClassName="editor-class"
        toolbarClassName="toolbar-class"
        customStyleMap={{
          'SMALLCAPS' : {
            fontVariant: 'small-caps'
          }
        }}
        toolbarCustomButtons={[
            <FontVariant
              editorState={editorState}
              onChange={(state)=> {setEditorState(state)}}
            ></FontVariant>
        ]}
        onTab={(e) => {
          e.stopPropagation();
          if (e.shiftKey) {
            dispatch({
              reverse: true,
              type: "NEXT ACTIVE BLOCK",
            });
          } else {
            dispatch({
              reverse: false,
              type: "NEXT ACTIVE BLOCK",
            });
          }
        }}
        onEditorStateChange={(e: EditorState) => {
          setEditorState(e);
          dispatch({
            blockId: block.id,
            text: convertHtmlToInnodom(draftToHtml(convertToRaw(e.getCurrentContent()))),
            type: "UPDATE BLOCK TEXT",
          });
        }}
      />}
    </div>
  );
};
TextEditor.displayName = "TextEditor";

export const convertInnodomToHtml= (blockText: string) => {

  blockText = blockText.replaceAll("<b>", "<strong>");
  blockText = blockText.replaceAll("</b>", "</strong>");
  blockText = blockText.replaceAll("<i>", "<em>");
  blockText = blockText.replaceAll("</i>", "</em>");
  blockText = blockText.replaceAll("<u>", "<ins>");
  blockText = blockText.replaceAll("</u>", "</ins>");
  blockText = blockText.replaceAll("<s>", "<del>");
  blockText = blockText.replaceAll("</s>", "</del>");

  var match = parseColorTag(blockText);

  while (!!match) {
    blockText = blockText.substring(0, match.start) + match.content + blockText.substring(match.end)
    match = parseColorTag(blockText);
  }

  var fontMatch = parseFontTag(blockText);
  while (!!fontMatch) {
    blockText = blockText.substring(0, fontMatch.start) + fontMatch.toHtml() + blockText.substring(fontMatch.end)
    fontMatch = parseFontTag(blockText);
  }

  return blockText;
}

export const convertHtmlToInnodom = (blockText: string) => {
  blockText = blockText.replaceAll("<strong>", "<b>");
  blockText = blockText.replaceAll("</strong>", "</b>");
  blockText = blockText.replaceAll("<em>", "<i>");
  blockText = blockText.replaceAll("</em>", "</i>");
  blockText = blockText.replaceAll("<ins>", "<u>");
  blockText = blockText.replaceAll("</ins>", "</u>");
  blockText = blockText.replaceAll("<del>", "<s>");
  blockText = blockText.replaceAll("</del>", "</s>");

  // draftToHtml seems to add a trailing \n every time it's invoked on the editor data
  const index = blockText.lastIndexOf("\n");
  if (index >=0) {
    blockText = blockText.substring(0, index) + blockText.substring(index+1, blockText.length);
  }


  blockText = blockText.replaceAll("\n", "<br>");


  // In the innodom we might have osmething like <font>blabla1<br />blabla2</font>
  // The output of the RTE will separate this into two spans because you can't have <br> in a span
  // so it'll look like : <span>blabla1</span><br><span>blabla2</span>
  // To restore it exactly to the original appearance we'd have to fuse the spans together 
  // But i think this is unlikely to hinder the predictor.  (confirm)
  // For now we just replace the tag so that the validation xml parser doesn't complain and 
  // make the text box red
  blockText = blockText.replaceAll("<br>", "<br />");

  // Not sure if this affects the predictions but the RTE seems to add these (confirm)
  blockText = blockText.replaceAll("<p>", "");
  blockText = blockText.replaceAll("</p>", "");

  var match = parseSpanTag(blockText);

  while (!!match) {
    blockText = blockText.substring(0, match.start) + match.toInnodom() + blockText.substring(match.end)
    match = parseSpanTag(blockText);
  }

  // Possible TODO : remove redundant color tags
  return blockText;
}

export class ColorTag {
  content: string;
  start?: number;
  end?: number;
}

export class FontTag {
  start?: number;
  end?: number;
  fontSize? : string;
  fontFamily?: string;
  fontVariant?: string;
  content?: string;
  color?:string;

  toHtml(): string {
    var styleElements = [];
    if (!!this.fontSize) {
      styleElements.push('font-size: ' + this.fontSize + ';')
    }
    if (!!this.fontFamily) {
      styleElements.push('font-family: ' + this.fontFamily + ';')
    }

    if (!!this.fontVariant) {
      styleElements.push('font-variant: ' + this.fontVariant + ';')
    }

    if (!!this.color) {
      styleElements.push('color: ' + this.color + ';')
    }

    var tagElements = ["<span"]
    if (styleElements.length > 0) {
      tagElements.push(' style="' + styleElements.join(" ") + '"')
    }
    tagElements.push(">");
    tagElements.push(this.content);
    tagElements.push("</span>");

    return tagElements.join("");
  }

  toInnodom(): string {
    var tagElements = ["<font "];
    if (!!this.fontSize) {
      tagElements.push('font-size="' + this.fontSize + '" ')
    }

    if (!!this.fontFamily) {
      tagElements.push('font-name="' + this.fontFamily.replaceAll(" ", "+") + '" ')
    }

    if (!!this.fontVariant) {
      tagElements.push('font-variant="' + this.fontVariant + '" ')
    }

    tagElements.push(">");
    tagElements.push(this.content);
    tagElements.push("</font>");

    if (this.color) {
      tagElements.unshift('<color rgb="' + this.color + '">')
      tagElements.push("</color>");
    }
    return tagElements.join("").replaceAll(" >", ">"); // It probably doesn't matter...
  }
}

export const parseSpanTag = (node: string) : FontTag | null => {
  var result : FontTag = new FontTag();
  
  result.start = node.indexOf("<span");
  if (result.start === -1) {
    return null;
  }
  var end = node.indexOf("</span>");
  result.end = end + "</span>".length;

  var spanTag = node.substring(result.start, end);
  result.content = spanTag.substring(spanTag.indexOf(">")+1)

  var fontSize = /font-size: ([0-9\.]*)(px)?;/.exec(spanTag);
  if (fontSize) {
    result.fontSize = Math.round((+fontSize[1] * (3/4))  * 100) / 100 + "pt"
  }

  var fontFamily = /font-family: ([^;]*);/.exec(spanTag);
  if (fontFamily) {
    result.fontFamily = fontFamily[1];
  }

  var fontVariant = /font-variant: ([^;]*);/.exec(spanTag);
  if (fontVariant && fontVariant[1] != "normal") {
    result.fontVariant = fontVariant[1];
  }


  var colorMatches = /color: rgb\(([^,]*),([^,]*),([^,]*)\);/.exec(spanTag);
  if (colorMatches) {
    var color = new Color(+colorMatches[1], +colorMatches[2], +colorMatches[3]);
    result.color = color.toHexString();
  }
  return result;
}

// Returns the first color tag in a block
export const parseColorTag = (node: string) : ColorTag | null => {
  var result : ColorTag = new ColorTag();
  
  result.start = node.indexOf("<color");
  if (result.start === -1) {
    return null;
  }
  var end = node.indexOf("</color>");
  result.end = end + "</color>".length;

  var colorTag = node.substring(result.start, end);
  var color = /rgb="([^"]*)"/.exec(colorTag)[1];

  var match = parseFontTag(colorTag);
  while (!!match) {
    match.color = color;
    colorTag = colorTag.substring(0, match.start) + match.toHtml() + colorTag.substring(match.end)
    match = parseFontTag(colorTag);
  }

  result.content = colorTag.substring('<color rgb="#123456">'.length);

  return result;
}



// Returns the first font tag in a block
export const parseFontTag = (node: string) : FontTag | null => {
  var result : FontTag = new FontTag();
  
  result.start = node.indexOf("<font");
  if (result.start === -1) {
    return null;
  }
  var end = node.indexOf("</font>");
  result.end = end + "</font>".length;

  var fontTag = node.substring(result.start, end);
  var closingBracketIndex = fontTag.indexOf(">");
  result.content = fontTag.substring(closingBracketIndex+1)
  fontTag = fontTag.substring(0, closingBracketIndex);
  var fontSize = /font-size="([^"]*)"/.exec(fontTag);
  if (fontSize) {
    result.fontSize = fontSize[1];
    if (result.fontSize.includes("pt")) {
      result.fontSize = Math.round((+result.fontSize.substring(0, result.fontSize.indexOf("pt")) * (4/3))  * 100) / 100 + "px"
    }
  } else {
    result.fontSize = "12";
  }

  var fontFamily = /font-name="([^"]*)"/.exec(fontTag);
  if (fontFamily) {
    result.fontFamily = fontFamily[1].replaceAll("+", " ");
  } else {
    result.fontFamily = "TimesNewRomanPSMT"
  }

  var fontVariant = /font-variant="([^"]*)"/.exec(fontTag);
  if (fontVariant) {
    result.fontVariant = fontVariant[1];
  }
  return result;
}